import { useEffect, useEffectEvent } from "react";
import { useChatPage } from "./useChatPage";

const MIN_PREFETCH_DISTANCE_PX = 320;

export function useLoadPreviousMessages(viewport: HTMLElement | null) {
    const {
        canFetchPrevious,
        isFetchingPrevious,
        fetchPreviousMessages,
        resetPreviousFetchFailure,
        isPending,
        isFetchedAfterMount,
    } = useChatPage();

    const syncPrefetch = useEffectEvent(() => {
        if (!viewport || isPending || !isFetchedAfterMount) {
            return;
        }

        const distance = Math.max(MIN_PREFETCH_DISTANCE_PX, viewport.clientHeight);

        if (viewport.scrollTop > distance) {
            resetPreviousFetchFailure();
            return;
        }

        if (!canFetchPrevious || isFetchingPrevious) {
            return;
        }

        fetchPreviousMessages();
    });

    useEffect(
        function prefetchWhileScrollingUp() {
            if (!viewport) {
                return;
            }

            let frame = 0;
            function onScroll() {
                if (frame !== 0) {
                    return;
                }

                frame = window.requestAnimationFrame(() => {
                    frame = 0;
                    syncPrefetch();
                });
            }

            viewport.addEventListener("scroll", onScroll, { passive: true });

            return () => {
                window.cancelAnimationFrame(frame);
                viewport.removeEventListener("scroll", onScroll);
            };
        },
        [viewport],
    );
}
